import { defineStore, acceptHMRUpdate } from 'pinia';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import uniqBy from 'lodash/uniqBy';
import groupBy from 'lodash/groupBy';

import { getUserInsights, convertGeneratedInsightToMarkdown } from '@/insights/full-page-insights/utils/data.utils';
import { usePagesStore } from '@/stores/pages.store';
import { useAppsStore } from '@/stores/apps.store';
import { useFeaturesStore } from '@/stores/features.store';
import { useStarsStore } from '@/stores/stars.store';
import { useTrackTypesStore } from '@/stores/track-types.store';
import { useUserSettingsStore } from '@/stores/user-settings.store';
import { useHistoryStore } from '@/stores/history.store';
import { useCoreEventsStore } from '@/stores/core-events.store';
import { getAllFocusGroups, deleteInsightsSegmentById, getInsightsSegmentAppIds } from '@/insights/utils/utils';
import { getDefaultSegments } from '@/utils/segments';
import { hasSubscriptionFlag } from '@/data/flags/local-flags';
import LOADING_STATES from '@/constants/loading-states';

import { useSubscriptionStore } from '@/stores/subscription.store';

type InsightId = string | number;
type DailyInsightStatus = 'resolved' | 'rejected' | 'loading' | 'empty';
type EmptyContext = Record<string, never>;

export interface InsightFocusGroup {
    id: string;
    name?: string;
    isFocusGroup?: boolean;
    appId?: InsightId;
    appIds?: InsightId[];
    processing?: boolean;
    [key: string]: unknown;
}

interface InsightRecord {
    [key: string]: unknown;
}

interface GeneratedInsight {
    markdown?: string;
    [key: string]: unknown;
}

interface DailyInsightSummary {
    generatedInsights?: GeneratedInsight[];
    [key: string]: unknown;
}

interface DailyInsightsState {
    isFetching: boolean;
    data: DailyInsightSummary[] | null | undefined;
    hasError: boolean;
}

interface InsightsState {
    focusGroupsMap: Record<string, InsightFocusGroup>;
    fetchingFocusGroups: boolean;
    insights: InsightRecord[];
    insightsFetched: boolean;
    dailyInsights: DailyInsightsState;
}

interface ContextItemDetails {
    appId?: InsightId;
    name?: string;
}

interface ContextSourceItem extends ContextItemDetails {
    id?: InsightId;
    itemId?: InsightId;
    kind?: string;
    type?: string;
    itemType?: string;
}

export interface InsightsContextEvent extends ContextItemDetails {
    id: InsightId;
    kind: string;
}

type InsightsContextEvents = Record<string, InsightsContextEvent[]>;

export interface InsightsContext {
    events: InsightsContextEvents;
    segment: InsightId[];
}

interface InsightsSegment {
    id: InsightId;
    [key: string]: unknown;
}

interface UserInsightsSetting {
    value?: InsightsContext;
}

interface UserInsightsResponse {
    body: {
        summaries?: DailyInsightSummary[];
    };
}

interface Application {
    id?: string;
}

interface LoadAllFocusGroupsOptions {
    reFetch?: boolean;
}

interface DeleteInsightsSegmentOptions {
    segmentId: string;
    appId: string;
}

type ItemLookup = (id: InsightId) => ContextItemDetails | undefined;

function getItemByType (
    itemType: string,
    itemId: InsightId,
): ContextItemDetails | null | undefined {
    if (itemType === 'Page') {
        return (usePagesStore().pageById as ItemLookup)(itemId);
    }
    if (itemType === 'Feature') {
        return (useFeaturesStore().featureById as ItemLookup)(itemId);
    }
    if (itemType === 'TrackType') {
        return (useTrackTypesStore().trackTypeById as ItemLookup)(itemId);
    }

    return null;
}

const { RESOLVED, REJECTED, LOADING, EMPTY } = LOADING_STATES as {
    RESOLVED: 'resolved';
    REJECTED: 'rejected';
    LOADING: 'loading';
    EMPTY: 'empty';
};

function getInitialState (): InsightsState {
    return {
        focusGroupsMap: {},
        fetchingFocusGroups: false,
        insights: [],
        insightsFetched: false,
        dailyInsights: {
            isFetching: false,
            data: null,
            hasError: false,
        },
    };
}

export const useInsightsStore = defineStore('insights', {
    state: getInitialState,
    actions: {
        setFocusGroups ({ focusGroups }: { focusGroups: Record<string, InsightFocusGroup> }): void {
            this.focusGroupsMap = focusGroups;
        },
        setFetchingFocusGroups (isFetching: boolean): void {
            this.fetchingFocusGroups = isFetching;
        },
        setInsights ({ insights }: { insights: InsightRecord[] }): void {
            this.insights = insights;
            this.insightsFetched = true;
        },
        removeInsightsSegment ({ id }: { id: string }): void {
            delete this.focusGroupsMap[id];
        },
        setDailyInsightsFetching (isFetching: boolean): void {
            this.dailyInsights.isFetching = isFetching;
        },
        setDailyInsightsData (data: DailyInsightSummary[] | null | undefined): void {
            this.dailyInsights.data = data;
        },
        setDailyInsightsError (hasError: boolean): void {
            this.dailyInsights.hasError = hasError;
        },
        async loadAllFocusGroups (
            { reFetch = false }: LoadAllFocusGroupsOptions = {},
        ): Promise<InsightFocusGroup[]> {
            if (!useSubscriptionStore().hasInsights || (!isEmpty(this.focusGroupsMap) && !reFetch)) {
                return Object.values(this.focusGroupsMap);
            }
            try {
                this.setFetchingFocusGroups(true);
                const focusGroups = (await getAllFocusGroups()) as InsightFocusGroup[];

                focusGroups.unshift({ id: '0', isFocusGroup: true, name: 'Everyone' });

                const insightsSegmentToAppIdsMap = (await getInsightsSegmentAppIds()) as Record<string, InsightId[]>;
                const insightsSegmentsWithAppIds = focusGroups.map((insightsSegment) => {
                    return {
                        ...insightsSegment,
                        appIds: insightsSegmentToAppIdsMap[insightsSegment.id] || [],
                    };
                });

                this.setFocusGroups({ focusGroups: keyBy(insightsSegmentsWithAppIds, 'id') });
                this.setFetchingFocusGroups(false);

                return insightsSegmentsWithAppIds;
            } catch (ex) {
                throw new Error(`Failed to load Insights segments: ${ex}`);
            }
        },
        async deleteInsightsSegment ({ segmentId, appId }: DeleteInsightsSegmentOptions): Promise<void> {
            try {
                await deleteInsightsSegmentById(segmentId, appId);
                this.removeInsightsSegment({ id: segmentId });
            } catch {
                throw new Error(`Failed to delete Insights segment: ${segmentId}`);
            }
        },
        async getDailyInsights (body: InsightsContext): Promise<void> {
            if (this.dailyInsights.isFetching) {
                return;
            }

            // Clear existing data to force new fetch when context changes
            if ((this.dailyInsights.data?.length ?? 0) > 0) {
                this.setDailyInsightsData(null);
            }
            this.setDailyInsightsFetching(true);
            try {
                const resp = (await getUserInsights(body)) as UserInsightsResponse;
                const { summaries } = resp.body;
                summaries?.forEach((summary) => {
                    summary.generatedInsights?.forEach((insight) => {
                        insight.markdown = convertGeneratedInsightToMarkdown(insight) as string;
                    });
                });
                this.setDailyInsightsData(summaries);
                this.setDailyInsightsError(false);
                this.setDailyInsightsFetching(false);
            } catch (error) {
                this.setDailyInsightsError(true);
                this.setDailyInsightsFetching(false);
                throw new Error(`Failed to fetch daily insights: ${error}`);
            }
        },
    },
    getters: {
        getFocusGroups: (state): Record<string, InsightFocusGroup> => state.focusGroupsMap,
        getFocusGroupIds: (state): string[] => Object.keys(state.focusGroupsMap),
        getFocusGroupName: (state) => (id: string): string =>
            (id === '0' ? 'Everyone' : state.focusGroupsMap[id]?.name || 'Unknown'),
        getFocusGroupById: (state) => (id: string): InsightFocusGroup | undefined => state.focusGroupsMap[id],
        getDailyInsightStatus: (state): DailyInsightStatus => {
            if (state.dailyInsights.hasError) {
                return REJECTED;
            }
            if ((state.dailyInsights.data?.length ?? 0) > 0) {
                return RESOLVED;
            }
            if (state.dailyInsights.data?.length === 0) {
                return EMPTY;
            }

            return LOADING;
        },
        getDailyInsightsContext (): InsightsContext {
            const userInsightsContext = useUserSettingsStore().settingByName(
                'insights-context',
                true,
            ) as unknown as UserInsightsSetting | null | undefined;
            if (userInsightsContext) {
                const { value } = userInsightsContext;
                if (value) {
                    const { events, segment } = value;
                    const keys = Object.keys(events);
                    if (keys.length) {
                        const undeletedEvents = keys.map((key) => {
                            const eventsArray = events[key].filter(
                                (event) => getItemByType(event.kind, event.id) !== undefined,
                            );
                            if (eventsArray.length) {
                                return eventsArray;
                            }

                            return [];
                        });

                        // Check if we have any valid events after filtering
                        const hasValidEvents = undeletedEvents.some((eventArray) => eventArray.length > 0);

                        if (hasValidEvents) {
                            return {
                                events: groupBy(
                                    undeletedEvents.flatMap((eventsForType) => eventsForType),
                                    'kind',
                                ),
                                segment,
                            };
                        }
                    }
                }
            }

            // Default fallback context
            const defaultSegments = getDefaultSegments() as Record<string, InsightsSegment>;
            let defaultSegment;
            if (hasSubscriptionFlag('showAnonymous')) {
                defaultSegment = defaultSegments.everyoneExcludingAnonymous;
            } else {
                defaultSegment = defaultSegments.everyone;
            }
            const selectedContextEventsByType = { ...this.getSelectedContextEventsByType };
            const events: InsightsContextEvents = {};
            for (const key in selectedContextEventsByType) {
                if (Object.prototype.hasOwnProperty.call(selectedContextEventsByType, key)) {
                    events[key.toLowerCase()] = selectedContextEventsByType[key];
                }
            }

            return {
                events,
                segment: [defaultSegment.id],
            };
        },
        getDefaultInsightsSegment (): InsightsSegment {
            const defaultSegments = getDefaultSegments() as Record<string, InsightsSegment>;
            let defaultSegment;
            if (hasSubscriptionFlag('showAnonymous')) {
                defaultSegment = defaultSegments.everyoneExcludingAnonymous;
            } else {
                defaultSegment = defaultSegments.everyone;
            }

            return defaultSegment;
        },
        getDefaultContextEvents (): InsightsContextEvent[] | EmptyContext | Error {
            try {
                const defaultSelectedApplication = (
                    useAppsStore().allWebPlusIntegratedMobileApps as Application[] | undefined
                )?.[0] || null;
                const { id } = defaultSelectedApplication as Application;
                if (!id || useStarsStore().fetching) {
                    return {};
                }
                const coreEventsStore = useCoreEventsStore();
                const coreEventPages = coreEventsStore.coreEventListByKindAndApp(
                    'Page',
                    parseInt(id),
                ) as ContextSourceItem[];
                const coreEventFeatures = coreEventsStore.coreEventListByKindAndApp(
                    'Feature',
                    parseInt(id),
                ) as ContextSourceItem[];
                const coreEventTrackTypes = coreEventsStore.coreEventListByKindAndApp(
                    'TrackType',
                    parseInt(id),
                ) as ContextSourceItem[];
                const historyItems = useHistoryStore().allItemHistory([
                    'featureHistory',
                    'trackTypeHistory',
                    'pageHistory',
                ]) as ContextSourceItem[];
                const starPages = useStarsStore().getStarsByKind('Page') as ContextSourceItem[];
                const starFeatures = useStarsStore().getStarsByKind('Feature') as ContextSourceItem[];
                const starTrackTypes = useStarsStore().getStarsByKind('TrackType') as ContextSourceItem[];

                const selectedContext: ContextSourceItem[] = [
                    ...starTrackTypes,
                    ...starFeatures,
                    ...starPages,
                    ...historyItems,
                    ...coreEventFeatures,
                    ...coreEventTrackTypes,
                    ...coreEventPages,
                ].map((item) => {
                    return {
                        ...item,
                        id: item.id || item.itemId,
                        kind: item.kind || item.type || item.itemType,
                    };
                });
                const uniqueSelectedContext = uniqBy(selectedContext, 'id');

                let attempts = 0;
                const maxAttempts = uniqueSelectedContext.length; // Prevent infinite loops
                const contextItem: InsightsContextEvent[] = [];

                while (contextItem.length < 3 && attempts < maxAttempts) {
                    const item = uniqueSelectedContext.shift();
                    attempts++;

                    const type = item?.type || item?.kind || item?.itemType;
                    const itemId = item?.id || item?.itemId;

                    if (type && itemId) {
                        const itemDetails = getItemByType(type, itemId);
                        if (itemDetails) {
                            contextItem.push({
                                id: itemId,
                                kind: type,
                                appId: itemDetails.appId,
                                name: itemDetails.name,
                            });
                        }
                    }
                }

                return contextItem;
            } catch (error) {
                return error as Error;
            }
        },
        getSelectedContextEventsByType (): InsightsContextEvents {
            const defaultContext = this.getDefaultContextEvents;
            if (!defaultContext) return {};

            return groupBy(defaultContext as InsightsContextEvent[], 'kind');
        },
        getSelectedContextEventsByAppId (): Record<string, InsightsContextEvent[]> {
            if (!this.getDailyInsightsContext?.events) return {};
            const contextEvents: InsightsContextEvent[] = [];
            Object.keys(this.getDailyInsightsContext?.events).forEach((key) => {
                const value = this.getDailyInsightsContext?.events[key];
                contextEvents.push(...value);
            });
            if (!contextEvents) return {};

            return groupBy(contextEvents, 'appId');
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useInsightsStore, module.hot));
}
